import type { IntakeFormSection, SignatureField, V2UploadKind } from '../../api/bookingApi';
import type { FileUploadPolicy } from './FileUploadField';

// Normalised, validated view of a `form_package_v2` link (see validateV2Package.ts).
// Everything the renderer needs and nothing it must not trust: ids, kinds,
// requiredness, and the frozen patient-visible content.

export interface V2AgreementItem {
  kind: 'agreement';
  /** `text`: agreement text + signature_fields (legacy shape). `template`: authored in the
   *  form builder — `static_text` paragraphs plus signature/date/… fields in `sections`. */
  mode: 'text' | 'template';
  instanceItemId: string;
  ordinal: number;
  name: string;
  required: boolean;
  content: string;                 // text mode; '' in template mode
  signatureFields: SignatureField[];
  sections: IntakeFormSection[];   // template mode; [] in text mode
}

export interface V2FormItem {
  /** `insurance_update`: the staff-sent insurance-only form (backend#1602), a cut of
   *  the intake's identity, insurance and card fields rendered like any other form. */
  kind: 'intake' | 'custom' | 'insurance_update';
  instanceItemId: string;
  ordinal: number;
  name: string;
  required: boolean;
  insuranceStatusNoRelaxesRequired?: boolean;
  sections: IntakeFormSection[];
}

export type V2Item = V2AgreementItem | V2FormItem;

export interface V2Package {
  packageInstanceId: string;
  items: V2Item[];            // sorted by ordinal
  hasAgreements: boolean;
  uploadBudget: number;       // signatures + files this package can reference at most
}

/** One drawn signature or picked file, addressed by where it belongs in the package. */
export type SlotKey = string;

export const slotKeys = {
  agreementSig: (itemId: string, index: number): SlotKey => `ag:${itemId}:${index}`,
  fieldSig: (itemId: string, fieldKey: string): SlotKey => `sig:${itemId}:${fieldKey}`,
  fieldFile: (itemId: string, fieldKey: string): SlotKey => `file:${itemId}:${fieldKey}`,
  consent: 'consent' as SlotKey,
};

export interface StagedUpload {
  uploadId: string;
  uploadKind: V2UploadKind;
  byteSize: number;
  previewUrl?: string;   // in-memory only (data URL for signatures)
  fileName?: string;
}

/** The only `file_upload` field keys v2 can stage: they double as the upload kind. */
export const FILE_UPLOAD_FIELD_KEYS = [
  'insurance_card_front',
  'insurance_card_back',
  'photo_id_front',
  'photo_id_back',
] as const;
export type FileUploadFieldKey = typeof FILE_UPLOAD_FIELD_KEYS[number];

export function isFileUploadFieldKey(key: string): key is FileUploadFieldKey {
  return (FILE_UPLOAD_FIELD_KEYS as readonly string[]).indexOf(key) !== -1;
}

export const MAX_UPLOADS_PER_SUBMISSION = 20;
export const SIGNATURE_MAX_BYTES = 1024 * 1024;

/** v2 staging accepts images only (no PDF) — backend#2062 will widen this.
 *  HEIC/HEIF are accepted CLIENT-side only: prepareUploadFile transcodes them
 *  to JPEG before staging, so the bytes the server sees stay png/jpeg/webp. */
export const V2_FILE_UPLOAD_POLICY: FileUploadPolicy = {
  mimeTypes: ['image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif'],
  accept: '.png,.jpg,.jpeg,.webp,.heic,.heif',
  maxBytes: 10 * 1024 * 1024,
  helpText: 'PNG, JPG, WebP, or HEIC (max 10MB)',
  typeError: 'Invalid file type. Please upload a PNG, JPG, or WebP image.',
  sizeError: 'File is too large. Maximum size is 10MB.',
};
